/* eslint-disable no-relative-import-paths/no-relative-import-paths */
import { DOWNLOAD_POSTER_PREVIEW_URL } from '../constants';
import { Country } from '../enums';

export const Currency = {
  Czk: 'CZK',
  Eur: 'EUR',
};

export enum NodeEnv {
  Prod = 'prod',
  Dev = 'dev',
  Local = 'local',
  PrCi = 'PR-CI',
  QaDev = 'QA-DEV',
}

export const getParsedBoolean = (value: string | undefined, envName: string, isValueRequired?: boolean): boolean => {
  if (!['true', 'false'].includes(value as string)) {
    if (!isValueRequired && !value) {
      return false;
    }
    throw new Error(`Env variable ${envName} of type boolean has invalid value ${value}`);
  }

  return value === 'true';
};

const getParsedEnum = <T>(value: string | undefined, enumValues: T[], envName: string): T => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type enum is missing`);
  }

  if (!enumValues.includes(value as any)) {
    throw new Error(
      `Env variable ${envName} with value: ${value} is not a valid enum value. Valid values are: ${enumValues}`,
    );
  }
  return value as T;
};

const getParsedString = (value: string | undefined, envName: string): string => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type string is missing`);
  }
  return value;
};

// Lets keep it here for future reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getParsedNumber = (value: string | undefined, envName: string): number => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type number is missing`);
  }
  const parsed = Number(value);
  if (isNaN(parsed)) {
    throw new Error(`Env variable ${envName} with value: ${value} is not a number`);
  }
  return parsed;
};

// Lets keep it here for future reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getParsedArrayOfNumbers = (value: string | undefined, envName: string): number[] => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type array of numbers is missing`);
  }
  try {
    return value.split(',').map((num) => {
      const parsed = Number(num.trim());
      if (isNaN(parsed)) {
        throw new Error(`Env variable ${envName} with value: ${value} has invalid array item ${num}  `);
      }
      return parsed;
    });
  } catch (error) {
    throw new Error(`Env variable ${envName} with value: ${value} has invalid array format`);
  }
};

// Lets keep it here for future reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getParsedArrayOfEnumValues = <T>(value: string | undefined, enumValues: T[], envName: string): T[] => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type array of numbers is missing`);
  }

  try {
    return value.split(',').map((item) => {
      const parsed = getParsedEnum(item.trim(), enumValues, envName);

      return parsed;
    });
  } catch (error) {
    throw new Error(`Env variable ${envName} with value: ${value} has invalid array format`);
  }
};

const getParsedUrl = (value: string | undefined, envName: string): string => {
  if (!value) {
    throw new Error(`Env variable ${envName} of type url is missing`);
  }
  try {
    new URL(value);
    return value;
  } catch (error) {
    throw new Error(`Env variable ${envName} with value: ${value} is not a valid url`);
  }
};

type EnvObject = {
  businessEnv: NodeEnv;
  country: Country;
  publicApiBaseUrl: string;
  amioChannelId: string;
  getPosterPreviewUrl: (numericId: number) => string;
  publicBaseUrl: string;
  redirectUrl: string;
  isMixpanelEnabled: boolean;
  isMixpanelInDebugMode: boolean;
  isGtmEnabled: boolean;
  isAnalyticInDebugMode: boolean;
  isInCypressTesting: boolean;
};

const publicApiBaseUrl = getParsedUrl(process.env.NEXT_PUBLIC_API_BASE_URL, 'NEXT_PUBLIC_API_BASE_URL');

export const envs: EnvObject = {
  businessEnv: getParsedEnum(process.env.NEXT_PUBLIC_BUSINESS_ENV, Object.values(NodeEnv), 'NEXT_PUBLIC_BUSINESS_ENV'),
  country: getParsedEnum(process.env.NEXT_PUBLIC_COUNTRY, Object.values(Country), 'NEXT_PUBLIC_COUNTRY'),
  publicApiBaseUrl,
  publicBaseUrl: getParsedUrl(process.env.NEXT_PUBLIC_BASE_URL, 'NEXT_PUBLIC_BASE_URL'),
  amioChannelId: getParsedString(process.env.NEXT_PUBLIC_AMIO_CHANNEL_ID, 'NEXT_PUBLIC_AMIO_CHANNEL_ID'),
  getPosterPreviewUrl: (numericId: number) =>
    DOWNLOAD_POSTER_PREVIEW_URL.replace(':numericId', `${numericId}`).replace(':originUrl', publicApiBaseUrl),
  redirectUrl: getParsedUrl(process.env.NEXT_PUBLIC_REDIRECT_URL, 'NEXT_PUBLIC_REDIRECT_URL'),
  isMixpanelEnabled: getParsedBoolean(process.env.NEXT_PUBLIC_MIXPANEL_ENABLED, 'NEXT_PUBLIC_MIXPANEL_ENABLED'),
  isMixpanelInDebugMode: getParsedBoolean(process.env.NEXT_PUBLIC_MIXPANEL_DEBUG, 'NEXT_PUBLIC_MIXPANEL_ENABLED'),
  isGtmEnabled: getParsedBoolean(process.env.NEXT_PUBLIC_GTM_ENABLED, 'NEXT_PUBLIC_GTM_ENABLED'),
  isAnalyticInDebugMode: getParsedBoolean(process.env.NEXT_PUBLIC_IS_ANALYTIC_IN_DEBUG_MODE, 'NEXT_PUBLIC_GTM_ENABLED'),
  isInCypressTesting: getParsedBoolean(
    process.env.NEXT_PUBLIC_IS_IN_CYPRESS_TESTING,
    'NEXT_PUBLIC_IS_IN_CYPRESS_TESTING',
    false,
  ),
};
